import { FC, useEffect, useRef } from 'react';
import './index.scss';
import { Button } from '../ui/Button';
import { Icons } from '../ui/Icons';

type TPopupProps = {
  children: React.ReactNode;
  open: boolean;
  onClose?: () => void;
  title?: string;
  className?: string;
  positionVertical?: 'top' | 'bottom' | 'center';
};

export const Popup: FC<TPopupProps> = props => {
  const {
    children,
    open,
    onClose,
    title,
    className = '',
    positionVertical = 'top',
  } = props;
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const popupElement = popupRef.current;
      const isInsidePopup = popupElement?.contains(target);
      const isInsidePortalSelect = target?.closest('[data-popup-portal]');

      if (!isInsidePopup && !isInsidePortalSelect && onClose) {
        onClose();
      }
    };

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [open, onClose]);

  return (
    <div
      className={`popup ${className} ${open ? 'popup--open' : ''} ${
        positionVertical ? `popup--${positionVertical}` : ''
      }`}
    >
      <div ref={popupRef} className="popup__inner">
        {title && <h4 className="popup__title">{title}</h4>}
        <div className="popup__content">{children}</div>
        {onClose && (
          <Button className="popup__close" variant="link" onClick={onClose}>
            <Icons name="icon_close" />
          </Button>
        )}
      </div>
    </div>
  );
};
